<template>
  <div class="reservation-wrapper" v-if="!isCollected">
    <div v-if="loading" class="loading-spinner">
      <v-progress-circular indeterminate color="#00963f" size="32"></v-progress-circular>
    </div>

    <!-- Collapsed Banner -->
    <div v-else class="promo-banner-primary" @click="isExpanded = true">
      <span v-if="!hasReserved" class="savings-badge-banner">-{{ savingsPercentage }}%</span>
      <div class="banner-content">
        <div class="icon-container-primary">
          <v-icon class="icon" color="#FFFFFF">
            {{ hasReserved ? "mdi-receipt-text-check" : "mdi-gift" }}
          </v-icon>
        </div>
        <div class="text-content-primary">
          <p class="title">{{ hasReserved ? "Dein Duschtuch-Gutschein" : "Exklusive Mitglieder-Aktion" }}</p>
          <p class="subtitle">{{ hasReserved ? "Zur Abholung hier klicken" : "Jetzt dein Duschtuch sichern!" }}</p>
        </div>
        <div class="chevron-container">
          <v-icon class="chevron-icon-primary">mdi-chevron-right</v-icon>
        </div>
      </div>
      <div class="progress-bar-container" v-if="!hasReserved">
        <div class="progress-bar" :style="{ width: availabilityPercentage + '%' }" :class="progressBarClass"></div>
      </div>
    </div>

    <!-- Modal Overlay -->
    <transition name="modal-fade">
      <div v-if="isExpanded" class="modal-overlay" @click.self="isExpanded = false">
        <!-- Reservation Modal (Dark Mode) -->
        <div v-if="!hasReserved" class="modal-content">
          <button @click="isExpanded = false" class="close-button"><v-icon>mdi-close</v-icon></button>
          <v-img src="/img/einmalig/mikrofaserTuch.jpg" class="towel-image-modal"></v-img>
          <div class="modal-header">
            <h3 class="headline">DEAL: Mikrofaser Duschtuch 🚿</h3>
            <p class="subheadline">Nur solange der Vorrat reicht!</p>
          </div>

          <!-- Info Box -->
          <div class="info-box">
            <v-icon class="info-icon">mdi-information-outline</v-icon>
            <p class="info-text-modal">
              Reserviere dein Duschtuch direkt hier in der App. Du kannst es anschliessend bequem im Studio abholen und bezahlen.
            </p>
          </div>

          <div class="progress-section">
            <div class="progress-text">
              <span>Verfügbar:</span>
              <span class="font-weight-bold">{{ Math.round(availabilityPercentage) }}%</span>
            </div>
            <v-progress-linear
              :value="availabilityPercentage"
              :color="progressBarColor"
              background-color="#333"
              height="10"
              rounded></v-progress-linear>
          </div>

          <div class="quantity-section">
            <p class="quantity-label">Anzahl auswählen:</p>
            <div class="quantity-selector">
              <button @click="decreaseQuantity" class="quantity-btn">-</button>
              <span class="quantity-value">{{ quantity }}</span>
              <button @click="increaseQuantity" class="quantity-btn">+</button>
            </div>
          </div>

          <div class="price-display">
            <div>
              <span class="new-price">CHF {{ (6.9 * quantity).toFixed(2) }}</span>
              <span class="old-price">statt {{ (16.9 * quantity).toFixed(2) }}</span>
            </div>
            <span class="savings-badge">Du sparst {{ savingsPercentage }}%</span>
          </div>

          <div class="action-area">
            <v-btn @click="reserveTowel" :disabled="towelsRemaining < quantity || reservationInProgress" class="reserve-button" x-large>
              <v-progress-circular v-if="reservationInProgress" indeterminate size="28" color="white"></v-progress-circular>
              <span v-else> {{ quantity }} Stk. reservieren</span>
            </v-btn>
            <p v-if="towelsRemaining < quantity" class="sold-out-text">Nicht genügend Handtücher verfügbar.</p>
          </div>
        </div>

        <!-- Voucher Modal (Dark Mode) -->
        <div v-else class="modal-content voucher-view">
          <button @click="isExpanded = false" class="close-button"><v-icon>mdi-close</v-icon></button>
          <div class="voucher-header-dark">
            <h3 class="headline">Abhol-Gutschein</h3>
            <p class="subheadline">Zeige dies an der Theke</p>
          </div>
          <div class="quantity-display-dark">
            <span class="quantity-number-dark">{{ reservation.quantity }}</span>
            <span class="quantity-label-dark">Duschtuch<span v-if="reservation.quantity > 1">er</span></span>
          </div>
          <p class="voucher-info-text">Deine Reservierung ist aktiv. Du kannst sie hier stornieren, falls du die falsche Menge gewählt hast.</p>
          <div class="action-area">
            <v-btn @click="cancelReservation" :loading="cancellationInProgress" class="cancel-button" large text> Reservierung stornieren </v-btn>
          </div>
        </div>
      </div>
    </transition>
    <v-dialog v-model="expiryDialog" max-width="420" persistent content-class="expiry-dialog-transparent">
      <v-card dark class="expiry-dialog-card">
        <v-btn icon absolute top right @click="expiryDialog = false" class="close-btn-dialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-card-text class="pa-8 text-center">
          <div class="icon-container-dialog">
            <v-icon class="expiry-icon-dialog">mdi-timer-sand</v-icon>
          </div>

          <h2 class="expiry-headline mt-5">Reservierung läuft ab!</h2>

          <p class="expiry-main-text mt-4">Deine Reservierung ist nur noch</p>
          <div class="expiry-countdown-box">
            {{ expiryDaysRemaining }}
            <span class="day-label">Tag<span v-if="expiryDaysRemaining !== 1">e</span> aktiv</span>
          </div>

          <p class="expiry-subtext mt-4">Bitte hole dein Duschtuch bald im Studio ab, sonst wird die Reservierung automatisch storniert.</p>

          <v-btn class="verstanden-btn mt-6" x-large rounded block @click="expiryDialog = false"> Verstanden </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// The script part remains the same
import api from "../api"

export default {
  name: "TowelReservation",
  data() {
    return {
      loading: true,
      reservationInProgress: false,
      cancellationInProgress: false,
      towelsRemaining: 0,
      totalTowels: 4200,
      reservation: null, // Stores {id, status, quantity}
      isExpanded: false,
      quantity: 1,
      expiryDialog: false, // <-- NEU: Steuert das Popup
    }
  },
  computed: {
    expiryDaysRemaining() {
      if (!this.reservation || !this.reservation.expiryWarningStartDate || this.reservation.status !== "reserved") {
        return null
      }

      const getStartOfDay = (dateInput) => {
        const date = new Date(dateInput)
        date.setHours(0, 0, 0, 0)
        return date.getTime()
      }

      const warningStartTime = getStartOfDay(this.reservation.expiryWarningStartDate)

      const nowTime = getStartOfDay(new Date())

      const daysSinceWarning = (nowTime - warningStartTime) / (1000 * 60 * 60 * 24)

      const daysLeft = 5 - daysSinceWarning

      if (daysLeft < 0) return 0
      return daysLeft
    },
    isCollected() {
      return this.reservation && this.reservation.status === "collected"
    },
    hasReserved() {
      return this.reservation && this.reservation.status === "reserved"
    },
    availabilityPercentage() {
      if (!this.totalTowels || this.totalTowels === 0) return 0
      return (this.towelsRemaining / this.totalTowels) * 100
    },
    savingsPercentage() {
      const oldPrice = 16.9
      const newPrice = 6.9
      return Math.round(((oldPrice - newPrice) / oldPrice) * 100)
    },
    progressBarColor() {
      const percentage = this.availabilityPercentage
      if (percentage > 50) return "#00963f"
      if (percentage > 20) return "orange"
      return "red"
    },
    progressBarClass() {
      const percentage = this.availabilityPercentage
      if (percentage > 50) return "green"
      if (percentage > 20) return "orange"
      return "red"
    },
  },
  async created() {
    await this.fetchReservationStatus()
  },
  methods: {
    increaseQuantity() {
      if (this.quantity < 50) this.quantity++
    },
    decreaseQuantity() {
      if (this.quantity > 1) this.quantity--
    },
    async fetchReservationStatus() {
      try {
        this.loading = true
        const data = await api.getTowelActionStatus()
        if (data) {
          this.towelsRemaining = Number(data.towelsRemaining) || 0
          this.totalTowels = Number(data.totalTowels) || 4200
          this.reservation = data.userReservation
        }
      } catch (error) {
        console.error("Fehler beim Abrufen des Reservierungsstatus:", error)
      } finally {
        this.loading = false

        if (this.expiryDaysRemaining !== null && this.expiryDaysRemaining > 0) {
          this.expiryDialog = true
        }
      }
    },
    async reserveTowel() {
      this.reservationInProgress = true
      try {
        await api.reserveTowel(this.quantity)
        this.isExpanded = false
        await this.fetchReservationStatus()
      } catch (error) {
        console.error("Fehler bei der Reservierung:", error)
      } finally {
        this.reservationInProgress = false
      }
    },
    async cancelReservation() {
      this.cancellationInProgress = true
      try {
        await api.cancelTowelReservation()
        this.isExpanded = false
        this.reservation = null
        this.quantity = 1 // Reset quantity
        await this.fetchReservationStatus()
      } catch (error) {
        console.error("Fehler bei der Stornierung:", error)
      } finally {
        this.cancellationInProgress = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* Scoped styles with light theme and primary color */
@import url("https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css");

.reservation-wrapper {
  padding: 16px;
  font-family: "Roboto", sans-serif;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60px;
}

/* Primary Color Banner */
.promo-banner-primary {
  background: linear-gradient(135deg, #00963f 0%, #00b04a 100%);
  color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 150, 63, 0.3);
  padding: 12px 16px;
  transition: all 0.3s ease-in-out;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  max-width: 700px;
  margin: auto;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 150, 63, 0.35);
  }
}

.reserved-banner-primary {
  background: #2c3e50;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.icon-container-primary {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.text-content-primary .title {
  font-weight: 700;
  font-size: 1rem;
  margin: 0;
  line-height: 1.2;
}

.text-content-primary .subtitle {
  font-size: 0.8rem;
  opacity: 0.8;
  margin: 0;
}

.chevron-icon-primary {
  color: rgba(255, 255, 255, 0.7);
}

.savings-badge-banner {
  position: absolute;
  top: 50%;
  right: 40px; /* Space for chevron */
  transform: translateY(-50%);
  background: #f39c12;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.8rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

/* Modal - Dark Theme */
.modal-content {
  max-height: 100vh;
  overflow: auto;
  background: #1e1e1e;
  color: white;
  width: 100%;
  max-width: 400px;
  border-radius: 20px 20px 0 0;
  padding: 20px;
  position: relative;
  animation: slide-up 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  color: white;
  cursor: pointer;
}
.headline {
  font-size: 1.8rem;
  font-weight: 900;
}
.subheadline {
  font-size: 1rem;
  color: #bdc3c7;
  margin-top: 4px;
}

/* NEW Info Box Styles */
.info-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  margin: 16px 0;
}
.info-icon {
  color: #00963f;
  font-size: 24px;
}
.info-text-modal {
  font-size: 0.85rem;
  color: #bdc3c7;
  line-height: 1.5;
  margin: 0;
}

.progress-section {
  margin: 24px 0;
}
.progress-text {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 1rem;
}

/* Quantity Selector - Dark */
.quantity-section {
  margin: 24px 0;
  text-align: center;
}
.quantity-label {
  font-size: 1rem;
  color: #bdc3c7;
  margin-bottom: 12px;
}
.quantity-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}
.quantity-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 2rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}
.quantity-value {
  font-size: 2.5rem;
  font-weight: 700;
  min-width: 50px;
  text-align: center;
}

/* Price Display with Savings Badge */
.price-display {
  text-align: center;
  margin: 24px 0;
}
.new-price {
  font-size: 2rem;
  font-weight: 900;
  color: #00963f;
}
.old-price {
  text-decoration: line-through;
  opacity: 0.6;
  margin-left: 10px;
  font-size: 1rem;
}
.savings-badge {
  display: inline-block;
  background: #f39c12;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.8rem;
  margin-left: 12px;
  vertical-align: middle;
}

/* Voucher View - Dark */
.voucher-view {
  text-align: center;
  padding-top: 30px;
  padding-bottom: 30px;
}
.quantity-display-dark {
  margin: 30px 0;
  .quantity-number-dark {
    font-size: 6rem;
    font-weight: 900;
    line-height: 1;
    color: #00963f;
    display: block;
  }
  .quantity-label-dark {
    display: block;
    font-size: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: white;
  }
}
.voucher-info-text {
  font-size: 0.9rem;
  color: #bdc3c7;
  max-width: 80%;
  margin: 0 auto 25px;
}
.cancel-button {
  color: #e74c3c !important;
  font-weight: bold;
}

/* --- Unchanged Base Styles --- */
.banner-content {
  display: flex;
  align-items: center;
  gap: 12px;
}
.text-content-primary {
  flex-grow: 1;
}
.chevron-container {
  animation: bounce-right 2s infinite;
}
.progress-bar-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: rgba(0, 0, 0, 0.1);
}
.progress-bar {
  height: 100%;
  transition: width 0.5s ease-in-out;
  &.green {
    background: #00963f;
  }
  &.orange {
    background: #f39c12;
  }
  &.red {
    background: #e74c3c;
  }
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.towel-image-modal {
  border-radius: 12px;
  margin: 10px auto 20px;
  width: 70%;
}
.modal-header {
  text-align: center;
}
.action-area {
  text-align: center;
  padding-top: 10px;
}
.reserve-button {
  width: 100%;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: none;
  background: linear-gradient(135deg, #00963f 0%, #00b04a 100%);
  color: white;
}
.sold-out-text {
  text-align: center;
  color: #e74c3c;
  font-weight: bold;
  margin-top: 12px;
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
@keyframes slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
@keyframes bounce-right {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateX(0);
  }
  40% {
    transform: translateX(5px);
  }
  60% {
    transform: translateX(3px);
  }
}
::v-deep .v-dialog__content .v-dialog {
  background: transparent;
  box-shadow: none;
}
::v-deep .v-dialog {
  border-radius: 28px;
}

.expiry-dialog-card {
  /* Dunkler Hintergrund, passend zum Modal */
  background: linear-gradient(145deg, #2c3e50, #1e1e1e) !important;
  border-radius: 28px !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden; /* Stellt sicher, dass der Inhalt die Rundung einhält */
}

.close-btn-dialog {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  /* Hintergrund passend zum Schließen-Button im Modal */
  background: rgba(255, 255, 255, 0.1) !important;
}

.icon-container-dialog {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  /* Kräftiger Farbverlauf für die Warnung (Farben aus deinem CSS) */
  background: linear-gradient(135deg, #f39c12, #e74c3c);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px auto 0;
  box-shadow: 0 0 25px rgba(243, 156, 18, 0.5);
  /* Animation hinzufügen */
  animation: pulse-warning 2s infinite ease-in-out;
}

.expiry-icon-dialog {
  font-size: 56px !important;
  color: white;
}

.expiry-headline {
  /* Schriftart passend zur Modal-Headline */
  font-size: 1.9rem;
  font-weight: 900;
  color: white;
  line-height: 1.3;
}

.expiry-main-text {
  font-size: 1.1rem;
  color: #bdc3c7; /* Heller Text, passend zum Modal-Subtext */
  margin: 0;
}

.expiry-countdown-box {
  font-size: 5rem; /* Richtig groß */
  font-weight: 900;
  color: #f39c12; /* Warning Orange */
  line-height: 1.1;
  margin: 16px 0;
}

.day-label {
  font-size: 1.2rem;
  font-weight: 500;
  color: #bdc3c7;
  display: block;
  margin-top: -10px;
}

.expiry-subtext {
  font-size: 0.95rem;
  color: #bdc3c7;
  opacity: 0.8;
  line-height: 1.5;
}

.verstanden-btn {
  /* Grüner Farbverlauf, passend zum "Reservieren"-Button */
  background: linear-gradient(135deg, #00963f 0%, #00b04a 100%) !important;
  color: white !important;
  font-weight: 700;
  font-size: 1.1rem !important;
  letter-spacing: 0.5px;
}

/* Keyframes für die Puls-Animation des Icons */
@keyframes pulse-warning {
  0% {
    transform: scale(1);
    box-shadow: 0 0 20px rgba(243, 156, 18, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 35px rgba(243, 156, 18, 0.7);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 20px rgba(243, 156, 18, 0.4);
  }
}
</style>
